/**
 * Pull the most useful human message out of an axios error.
 *
 * The backend's GlobalExceptionHandler returns
 *   { success: false, error: { code, message } }   (BusinessException)
 * or a plain Spring error body for binding/validation failures. Callers were
 * previously showing `err.message` ("Request failed with status code 400"),
 * which told the user nothing.
 */
export function apiErrorMessage(err, fallback = 'Something went wrong') {
  const status = err?.response?.status;
  const d = err?.response?.data;

  /*
   * 403 before the generic unwrap.
   *
   * The backend's ACCESS_DENIED body says "You don't have permission to access
   * this resource", which reads as a fault rather than a boundary — the user
   * cannot tell whether something broke or they were simply never meant to do
   * this. Say which, plainly, so nobody files a bug about working software.
   *
   * Only for 403. A 401 is a dead session, which api.js already handles by
   * refreshing the token and, failing that, bouncing to login.
   */
  if (status === 403) {
    return 'You don’t have permission to do that. If you need it, ask a Dealer Principal.';
  }

  return (
    d?.error?.message
    || d?.message
    || (typeof d?.error === 'string' ? d.error : null)
    || d?.detail
    || err?.message
    || fallback
  );
}

/** True when the request was refused on permissions rather than failing. */
export const isForbidden = (err) => err?.response?.status === 403;

/** Machine-readable code (e.g. LOST_REASON_REQUIRED, APPOINTMENT_CONFLICT). */
export function apiErrorCode(err) {
  const d = err?.response?.data;
  return d?.error?.code || d?.code || null;
}
